import { config } from '../../config';
import { waveformToDataURL } from './waveformGenerator';
import { AudioBufferSource, Output, Mp4OutputFormat, Mp3OutputFormat, BufferTarget, canEncodeAudio } from 'mediabunny';
import { registerMp3Encoder } from '@mediabunny/mp3-encoder';

interface AudioMetadata {
  format: string;
  bitrate: number;
  duration: number;
  sampleRate: number;
  needsConversion: boolean;
}

interface ProcessedAudioResult {
  audioFile: File | Blob;
  waveformDataUrl: string;
  metadata: AudioMetadata;
  s3AudioKey?: string;
  s3WaveformKey?: string;
}

/**
 * Check audio file format and bitrate using Web Audio API
 */
export async function checkAudioMetadata(file: File): Promise<AudioMetadata> {
  const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
  
  try {
    const arrayBuffer = await file.arrayBuffer();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
    
    // Estimate bitrate from file size and duration
    const fileSizeInBits = file.size * 8;
    const durationInSeconds = audioBuffer.duration;
    const estimatedBitrate = Math.round(fileSizeInBits / durationInSeconds);
    
    // Get file extension
    const fileExtension = file.name.split('.').pop()?.toLowerCase() || '';
    const format = getFormatFromMimeTypeOrExtension(file.type, fileExtension);
    
    // Check if conversion is needed
    // MP4/M4A (AAC in MP4 container) and MP3 are optimal formats
    // Raw AAC is NOT optimal due to limited browser support
    const isOptimalFormat = ['mp3', 'm4a', 'mp4'].includes(format);
    const isOptimalBitrate = estimatedBitrate <= 200000; // 200kbps
    const needsConversion = !isOptimalFormat || !isOptimalBitrate;
    
    return {
      format,
      bitrate: estimatedBitrate,
      duration: durationInSeconds,
      sampleRate: audioBuffer.sampleRate,
      needsConversion
    };
  } finally {
    await audioContext.close();
  }
}

function getFormatFromMimeTypeOrExtension(mimeType: string, extension: string): string {
  // Map MIME types to formats
  const mimeToFormat: Record<string, string> = {
    'audio/mpeg': 'mp3',
    'audio/mp3': 'mp3',
    'audio/wav': 'wav',
    'audio/wave': 'wav',
    'audio/ogg': 'ogg',
    'audio/mp4': 'm4a',
    'audio/m4a': 'm4a',
    'audio/aac': 'aac',
    'audio/webm': 'webm'
  };
  
  if (mimeType && mimeToFormat[mimeType]) {
    return mimeToFormat[mimeType];
  }
  
  // Fallback to extension
  return extension || 'unknown';
}

/**
 * Detect supported audio formats - prioritize AAC/MP4, fallback to MP3
 */
export async function detectSupportedAudioFormat(): Promise<'aac' | 'mp3' | 'unsupported'> {
  try {
    // Check AAC support first (preferred format for quality and compression)
    const aacSupported = await canEncodeAudio('aac');
    if (aacSupported) {
      console.log('Browser supports AAC encoding - using high-quality AAC/MP4');
      return 'aac';
    }
  } catch (error) {
    console.log('AAC encoding not available:', error);
  }
  
  try {
    // Check native MP3 support
    const mp3Supported = await canEncodeAudio('mp3');
    if (mp3Supported) {
      console.log('Browser supports native MP3 encoding');
      return 'mp3';
    } else {
      // Register MP3 encoder extension for browsers without native support
      console.log('Registering MP3 encoder extension...');
      registerMp3Encoder();
      console.log('MP3 encoder registered successfully');
      return 'mp3';
    }
  } catch (error) {
    console.error('MP3 encoding setup failed:', error);
    return 'unsupported';
  }
}

/**
 * Convert audio file using MediaBunny - supports both AAC and MP3
 */
export async function convertAudioWithMediaBunny(
  file: File,
  onProgress?: (progress: number) => void
): Promise<{ blob: Blob; format: 'aac' | 'mp3' }> {
  try {
    // Detect supported audio format
    const supportedFormat = await detectSupportedAudioFormat();
    if (supportedFormat === 'unsupported') {
      throw new Error('Your browser does not support audio encoding. Please try Chrome or Edge.');
    }
    
    // First decode the audio file
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const arrayBuffer = await file.arrayBuffer();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
    
    console.log(`Converting audio to ${supportedFormat.toUpperCase()}...`, {
      duration: audioBuffer.duration,
      sampleRate: audioBuffer.sampleRate,
      numberOfChannels: audioBuffer.numberOfChannels
    });
    
    // Create audio source from the decoded buffer
    const audioSource = new AudioBufferSource({
      codec: supportedFormat === 'aac' ? 'aac' : 'mp3',
      bitrate: 128000, // 128 kbps
    });
    
    // Create buffer target to receive the output
    const target = new BufferTarget();
    
    // Create output configuration based on supported format
    const output = new Output({
      format: supportedFormat === 'aac' ? new Mp4OutputFormat() : new Mp3OutputFormat(),
      target,
    });
    
    // Add audio track
    output.addAudioTrack(audioSource);
    
    // Track progress
    const startTime = performance.now();
    const audioDuration = audioBuffer.duration;
    let progressInterval: NodeJS.Timeout | null = null;
    
    // Report initial progress
    if (onProgress) {
      onProgress(0);
    }
    
    // Start the encoding process
    await output.start();
    
    // Set up progress tracking
    const estimatedEncodingTime = (audioDuration * 1000) / (supportedFormat === 'aac' ? 100 : 50); // AAC is faster
    
    progressInterval = setInterval(() => {
      const elapsed = performance.now() - startTime;
      const progress = Math.min(elapsed / estimatedEncodingTime, 0.95);
      
      if (onProgress) {
        onProgress(Math.round(progress * 100));
      }
    }, 50);
    
    try {
      // Add the AudioBuffer directly to the source
      await audioSource.add(audioBuffer);
      
      // Finalize the output
      await output.finalize();
      
      // Clear progress interval and report 100%
      if (progressInterval) {
        clearInterval(progressInterval);
      }
      if (onProgress) {
        onProgress(100);
      }
      
      // Get the result buffer
      const resultBuffer = target.buffer;
      if (!resultBuffer) {
        throw new Error('No output buffer available after encoding');
      }
      
      const mimeType = supportedFormat === 'aac' ? 'audio/mp4' : 'audio/mpeg';
      const blob = new Blob([resultBuffer], { type: mimeType });
      
      console.log(`MediaBunny ${supportedFormat.toUpperCase()} conversion complete:`, {
        originalSize: file.size,
        convertedSize: blob.size,
        compression: ((1 - blob.size / file.size) * 100).toFixed(1) + '%'
      });
      
      return { blob, format: supportedFormat };
      
    } catch (error) {
      if (progressInterval) {
        clearInterval(progressInterval);
      }
      throw error;
    } finally {
      await audioContext.close();
    }
    
  } catch (error) {
    console.error('Audio conversion failed:', error);
    throw new Error('Failed to encode audio');
  }
}

/**
 * Get S3 presigned URL for upload
 */
export async function getS3PresignedUrl(
  type: 'audio' | 'waveform',
  fileExtension: string
): Promise<{ presignedUrl: string; key: string }> {
  const response = await fetch(`${config.apiUrl}/generate-s3-presigned-url`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem('token')}`
    },
    body: JSON.stringify({
      type,
      fileExtension,
      bucket: type === 'audio' ? 'AWS_S3_LIBRARY' : 'AWS_S3_WAVEFORMS'
    })
  });
  
  if (!response.ok) {
    throw new Error('Failed to get presigned URL');
  }
  
  return await response.json();
}

/**
 * Upload file to S3 using presigned URL
 */
export async function uploadToS3(
  presignedUrl: string,
  file: Blob | File,
  contentType: string,
  onProgress?: (progress: number) => void
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        const progress = Math.round((event.loaded / event.total) * 100);
        onProgress(progress);
      }
    };
    
    xhr.onload = () => {
      if (xhr.status === 200 || xhr.status === 204) {
        resolve();
      } else {
        reject(new Error(`S3 upload failed with status: ${xhr.status}`));
      }
    };
    
    xhr.onerror = () => {
      reject(new Error('S3 upload failed'));
    };
    
    xhr.open('PUT', presignedUrl);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.send(file);
  });
}

/**
 * Process and upload audio file with all necessary conversions and uploads
 */
export async function processAndUploadAudio(
  file: File,
  onProgress?: (stage: string, progress: number) => void
): Promise<ProcessedAudioResult> {
  try {
    // Step 1: Check audio metadata
    onProgress?.('analyzing', 0);
    const metadata = await checkAudioMetadata(file);
    onProgress?.('analyzing', 100);
    
    let processedFile: File | Blob = file;
    let convertedFormat: 'aac' | 'mp3' | null = null;
    
    // Step 2: Convert if needed
    if (metadata.needsConversion) {
      onProgress?.('converting', 0);
      const conversionResult = await convertAudioWithMediaBunny(file, (progress) => {
        onProgress?.('converting', progress);
      });
      processedFile = conversionResult.blob;
      convertedFormat = conversionResult.format;
      onProgress?.('converting', 100);
      
      // Update metadata after conversion
      const newMetadata = await checkAudioMetadata(
        processedFile instanceof File ? processedFile : new File([processedFile], file.name)
      );
      Object.assign(metadata, newMetadata);
      // Update format to reflect the actual converted format
      metadata.format = convertedFormat;
    }
    
    // Step 3: Generate waveform
    onProgress?.('waveform', 0);
    const waveformDataUrl = await waveformToDataURL(
      processedFile instanceof File ? processedFile : new File([processedFile], file.name)
    );
    onProgress?.('waveform', 100);
    
    // Step 4: Get presigned URLs
    onProgress?.('preparing', 0);
    let audioExtension: string;
    let mimeType: string;
    
    if (metadata.needsConversion && convertedFormat) {
      // Use the actual converted format
      audioExtension = convertedFormat === 'aac' ? 'mp4' : 'mp3';
      mimeType = convertedFormat === 'aac' ? 'audio/mp4' : 'audio/mpeg';
    } else {
      // Keep original format
      audioExtension = file.name.split('.').pop() || 'mp3';
      mimeType = file.type;
    }
    
    const [audioPresigned, waveformPresigned] = await Promise.all([
      getS3PresignedUrl('audio', audioExtension),
      getS3PresignedUrl('waveform', 'png')
    ]);
    onProgress?.('preparing', 100);
    
    // Step 5: Upload audio to S3
    onProgress?.('uploading_audio', 0);
    await uploadToS3(
      audioPresigned.presignedUrl,
      processedFile,
      mimeType,
      (progress) => onProgress?.('uploading_audio', progress)
    );
    
    // Step 6: Convert waveform data URL to blob and upload
    onProgress?.('uploading_waveform', 0);
    const waveformBlob = await dataURLToBlob(waveformDataUrl);
    await uploadToS3(
      waveformPresigned.presignedUrl,
      waveformBlob,
      'image/png',
      (progress) => onProgress?.('uploading_waveform', progress)
    );
    
    return {
      audioFile: processedFile,
      waveformDataUrl,
      metadata,
      s3AudioKey: audioPresigned.key,
      s3WaveformKey: waveformPresigned.key
    };
  } catch (error) {
    console.error('Error processing audio:', error);
    throw error;
  }
}

/**
 * Convert data URL to blob
 */
async function dataURLToBlob(dataUrl: string): Promise<Blob> {
  const response = await fetch(dataUrl);
  return await response.blob();
}

/**
 * Save audio metadata to backend
 */
export async function saveAudioMetadata(
  metadata: {
    name: string;
    description?: string;
    audio_type: 'music' | 'background' | 'sound';
    is_private: boolean;
    s3_audio_key: string;
    s3_waveform_key: string;
    duration: number;
    copyright_confirmed: boolean;
  }
): Promise<void> {
  const response = await fetch(`${config.apiUrl}/audio-library/save-metadata`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem('token')}`
    },
    body: JSON.stringify({
      ...metadata,
      mod_approved: true // Default to true, moderate later if needed
    })
  });
  
  if (!response.ok) {
    throw new Error('Failed to save audio metadata');
  }
}